import { API } from '../../shared/constants/constants';
import {
  normalizeConfigMfaPayload,
  normalizeDisableMfaPayload,
  normalizePreferredMfaPayload,
  normalizeUserId,
  normalizeValidateMfaPayload,
} from '../../shared/utils/userNormalization';

interface FormData {
  userId: string;
  password?: string;
  phone?: string;
}

export type MFAMethod = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';

interface MFAResponse {
  data: any;
  status?: number;
}

export const getUserMFAInfo = async (userId: string): Promise<MFAResponse> => {
  try {
    const normalizedId = normalizeUserId(userId);
    const res = await API.get(`/user/${normalizedId}/mfaSettings`, {});
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message ?? 'Error occurred when getting MFA info');
  }
};

export const configUserMFA = async (formData: FormData, method: MFAMethod = 'SMS_MFA'): Promise<MFAResponse> => {
  try {
    const normalizedId = normalizeUserId(formData.userId);
    const payload = normalizeConfigMfaPayload({
      method,
      password: formData.password,
      phone: formData.phone,
    });
    const res = await API.patch(`/user/${normalizedId}/configmfa`, payload);
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message ?? 'Error occurred when updating MFA info');
  }
};

export const validateUserMFACode = async (
  userId: string,
  session: string | null,
  code: string,
  method: MFAMethod = 'SMS_MFA'
): Promise<MFAResponse> => {
  try {
    const normalizedId = normalizeUserId(userId);
    const requestBody = normalizeValidateMfaPayload({ code, method, ...(session ? { session } : {}) });
    const res = await API.post(`/user/${normalizedId}/validatemfa`, requestBody);
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message ?? 'Error occurred when validating MFA code');
  }
};

export const disableUserMFA = async (formData: FormData, method: MFAMethod = 'SMS_MFA'): Promise<MFAResponse> => {
  try {
    const normalizedId = normalizeUserId(formData.userId);
    const payload = normalizeDisableMfaPayload({ method });
    const res = await API.patch(`/user/${normalizedId}/disablemfa`, payload);
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message ?? 'Error occurred when validating MFA code');
  }
};

export const setPreferredUserMFA = async (userId: string, method: MFAMethod): Promise<MFAResponse> => {
  try {
    const normalizedId = normalizeUserId(userId);
    const payload = normalizePreferredMfaPayload({ method });
    const res = await API.patch(`/user/${normalizedId}/preferredmfa`, payload);
    return res.data;
  } catch (err: any) {
    throw new Error(err.response?.data?.message ?? 'Error occurred when updating preferred MFA method');
  }
};
